/**
 * Shared voucher helpers: the rules a voucher can be issued under, the checks the
 * API makes on them (mirrored here so a mistake is a form error instead of a
 * round trip), the plain-language explanation, and the date handling.
 *
 * Used by the Vouchers page and by the "Generate vouchers" prize mode in the
 * contest form, so the two can never disagree about what a rule means.
 */

export type VoucherUsageType = 'SINGLE_USE' | 'MULTI_USE';

export type VoucherStatus = 'ACTIVE' | 'PARTIALLY_USED' | 'REDEEMED' | 'EXPIRED' | 'VOID';

export const VOUCHER_STATUSES: VoucherStatus[] = ['ACTIVE', 'PARTIALLY_USED', 'REDEEMED', 'EXPIRED', 'VOID'];

export interface VoucherCurrency {
  code: string;
  name: string;
  symbol: string;
  usd_rate: number;
}

/**
 * Standard currencies supported across vouchers and campaign prizes.
 * Used as fallback or to normalize currencies when the API list is empty or partial.
 */
export const DEFAULT_VOUCHER_CURRENCIES: VoucherCurrency[] = [
  { code: 'USD', name: 'US Dollar', symbol: '$', usd_rate: 1 },
  { code: 'SAR', name: 'Saudi Riyal', symbol: 'ر.س', usd_rate: 3.75 },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', usd_rate: 3.67 },
  { code: 'QAR', name: 'Qatari Riyal', symbol: 'ر.ق', usd_rate: 3.64 },
  { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'د.ك', usd_rate: 0.31 },
  { code: 'BHD', name: 'Bahraini Dinar', symbol: 'د.ب', usd_rate: 0.38 },
  { code: 'OMR', name: 'Omani Rial', symbol: 'ر.ع', usd_rate: 0.385 },
  { code: 'EGP', name: 'Egyptian Pound', symbol: 'E£', usd_rate: 48.5 },
  { code: 'JOD', name: 'Jordanian Dinar', symbol: 'د.ا', usd_rate: 0.71 },
];

/** Merges API currencies with default list so essential currencies (like EGP) are always present */
export function mergeVoucherCurrencies(apiList: any): VoucherCurrency[] {
  const incoming: VoucherCurrency[] = Array.isArray(apiList) ? apiList : [];
  const map = new Map<string, VoucherCurrency>();
  DEFAULT_VOUCHER_CURRENCIES.forEach((c) => map.set(c.code.toUpperCase(), c));
  incoming.forEach((c) => {
    if (c && c.code) {
      const code = String(c.code).toUpperCase();
      const existing = map.get(code);
      map.set(code, {
        code,
        name: c.name || existing?.name || code,
        symbol: c.symbol || existing?.symbol || code,
        usd_rate: Number(c.usd_rate) > 0 ? Number(c.usd_rate) : (existing?.usd_rate || 1),
      });
    }
  });
  return Array.from(map.values());
}

/** Limits the API enforces. */
export const VOUCHER_MAX_QUANTITY = 10000;
export const VOUCHER_MIN_USES = 2;
export const VOUCHER_MAX_USES = 1000;
export const VOUCHER_MAX_FACE_VALUE = 1000000;
export const VOUCHER_MAX_YEARS_AHEAD = 10;

/** The rule fields, in the shape the forms hold them. */
export interface VoucherRuleValues {
  quantity: number | null;
  value: number | null;
  currency: string | null;
  expires: Date | null;
  usageType: VoucherUsageType;
  carry: boolean;
  maxUses: number | null;
}

/** An i18n key plus its interpolation values. */
export interface VoucherMessage {
  key: string;
  params?: Record<string, any>;
}

/** Midnight at the start of today, local time. */
export function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * The calendar day the client picked, as YYYY-MM-DD.
 *
 * Sent as a bare date on purpose: the API parses it as UTC midnight, so the
 * voucher expires on the same calendar day for everyone regardless of the
 * browser's time zone (an ISO string built from local midnight would slip a day
 * east of UTC).
 */
export function toDateParam(d: Date | null | undefined): string | null {
  if (!d || isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * The inverse of toDateParam for a value the API returned (an ISO timestamp):
 * the UTC calendar day, rebuilt as a local date so the picker shows the day that
 * was originally chosen.
 */
export function parseServerDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/**
 * Anything wrong with a set of voucher rules. Returns i18n keys (under
 * `vouchers.errors`); an empty list means the API will accept them.
 *
 * @param opts.quantityRequired  false where the quantity is not part of the form
 * @param opts.minExpiry         earliest acceptable expiry (default: tomorrow)
 */
export function validateVoucherRules(
  v: VoucherRuleValues,
  opts: { quantityRequired?: boolean; checkExpiry?: boolean } = {},
): string[] {
  const errors: string[] = [];
  const { quantityRequired = true, checkExpiry = true } = opts;

  if (quantityRequired) {
    const q = Number(v.quantity);
    if (!Number.isInteger(q) || q < 1 || q > VOUCHER_MAX_QUANTITY) errors.push('vouchers.errors.quantity');
  }

  const value = Number(v.value);
  if (v.value === null || v.value === undefined || (v.value as any) === '' || !isFinite(value) || value <= 0 || value > VOUCHER_MAX_FACE_VALUE) {
    errors.push('vouchers.errors.value');
  }

  if (!v.currency) errors.push('vouchers.errors.currency');

  if (checkExpiry) {
    if (!v.expires) {
      errors.push('vouchers.errors.expiryRequired');
    } else if (v.expires.getTime() <= startOfToday().getTime()) {
      // Today itself is refused: a bare date means midnight at the START of that
      // day, which has already passed.
      errors.push('vouchers.errors.expiryFuture');
    } else if (v.expires.getTime() > Date.now() + VOUCHER_MAX_YEARS_AHEAD * 366 * 86400000) {
      errors.push('vouchers.errors.expiryFar');
    }
  }

  if (v.usageType === 'MULTI_USE') {
    const hasMax = v.maxUses !== null && v.maxUses !== undefined && (v.maxUses as any) !== '';
    const max = Number(v.maxUses);
    if (!v.carry && !hasMax) {
      errors.push('vouchers.errors.maxUsesRequired');
    } else if (hasMax && (!Number.isInteger(max) || max < VOUCHER_MIN_USES || max > VOUCHER_MAX_USES)) {
      errors.push('vouchers.errors.maxUsesRange');
    }
  }

  return errors;
}

/** Trim a money figure to at most two decimals, without trailing zeros. */
export function formatAmount(n: number | string | null | undefined): string {
  const num = Number(n);
  if (!isFinite(num)) return '0';
  return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

/**
 * The plain-language description of what the chosen rules mean, built around a
 * worked example (an item costing 30% of the face value), because "carry over"
 * and "forfeited" mean little until they are shown with numbers.
 */
export function explainVoucher(v: Pick<VoucherRuleValues, 'value' | 'currency' | 'usageType' | 'carry' | 'maxUses'>): VoucherMessage {
  const value = Number(v.value) > 0 ? Number(v.value) : 100;
  const spent = Math.round(value * 0.3 * 100) / 100;
  const left = Math.round((value - spent) * 100) / 100;
  const params = {
    value: formatAmount(value),
    spent: formatAmount(spent),
    left: formatAmount(left),
    currency: v.currency || '',
    max: v.maxUses ?? '',
  };

  if (v.usageType !== 'MULTI_USE') return { key: 'vouchers.explain.single', params };

  const hasMax = v.maxUses !== null && v.maxUses !== undefined && (v.maxUses as any) !== '' && Number(v.maxUses) > 0;
  if (v.carry) {
    return { key: hasMax ? 'vouchers.explain.carryMax' : 'vouchers.explain.carry', params };
  }
  // Max uses is required here; until it is filled in, describe the rule without a number.
  return { key: hasMax ? 'vouchers.explain.noCarry' : 'vouchers.explain.noCarryNoMax', params };
}

/** Quote a CSV cell, and defuse spreadsheet formulas in text that starts with = + - @. */
export function csvCell(value: any): string {
  let s = value === null || value === undefined ? '' : String(value);
  if (/^[=+\-@]/.test(s) && isNaN(Number(s))) s = `'${s}`;
  return `"${s.replace(/"/g, '""')}"`;
}

/** Download text as a UTF-8 CSV file (with a BOM so Excel reads Arabic text correctly). */
export function downloadCsv(filename: string, rows: any[][]): void {
  const body = rows.map((r) => r.map(csvCell).join(',')).join('\r\n');
  const blob = new Blob(['﻿' + body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Drop empty filter values; the gateway passes params through untouched. */
export function cleanParams(params: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {};
  Object.keys(params).forEach((k) => {
    const v = params[k];
    if (v === null || v === undefined || v === '') return;
    out[k] = typeof v === 'string' ? v.trim() : v;
  });
  return out;
}
